
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { mapState } from 'vuex';
import { email } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
// @ts-ignore
import PublicPortfolioRequestTestimonialsModalQuery from './apollo/PublicPortfolioRequestTestimonialsModal.gql';
import CopyIcon from '~/assets/svg/copy.vue.svg';
import PublicPortfolioModal from '~/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { buildQuery, makeUrl } from '~/components/utils/browser/url';
import MultiselectForm from '~/components/common/MultiselectForm/MultiselectForm.vue';
import RequestTestimonialForPublicPortfolioCurrentUser from '~/apollo/mutations/Portfolio/RequestTestimonialForPublicPortfolioCurrentUser.mutations';
import { loadingState } from '~/components/common/constants';
import type { PublicPortfolioCurrentUser, UserContact } from '~/types/graphql';
import { PublicPortfolioCurrentUserQuery } from '~/apollo/queries/PublicPortfolio/PublicPortfolioCurrentUser.query';
import PublicPortfolioImportContacts from '~/components/partials/public-portfolio/PublicPortfolioImportContacts.vue';
import { uid } from '~/components/utils/core/uid';
import publicPortfolioContactsSyncMixin from '~/components/partials/public-portfolio/mixins/publicPortfolioContactsSyncMixin';

interface IItem {
  id: number;
  name: string;
}

interface IData {
  modalName: string;
  form: {
    emails: Array<{ id: string | number; email?: string; name: string }>;
    note: string;
  };
  loadingState: string;
  hasCopiedLink: boolean;
  contacts: UserContact[];
  searchQuery: string;
}

const DEFAULT_NOTE =
  'Hi\n' +
  "I'm using Bravado to build a portfolio that showcases the sales work I've done with clients. Would you provide a testimonial about the work we've done together?\n" +
  'Thank you!';

export default Vue.extend({
  name: 'PublicPortfolioRequestTestimonialModal',
  components: {
    MultiselectForm,
    PublicPortfolioModal,
    BravadoTextInputV3,
    BravadoText,
    BravadoButtonV3,
    CopyIcon,
    PublicPortfolioImportContacts,
  },
  mixins: [validationMixin, publicPortfolioContactsSyncMixin],
  props: {
    testifySign: {
      type: String,
      default: '',
    } as PropOptions<string>,
  },
  apollo: {
    contacts: {
      query: PublicPortfolioRequestTestimonialsModalQuery,
      update: data => data.currentUser.contacts,
      skip(): boolean {
        // @ts-ignore
        return this.contactsSynced !== this.hasTriggeredContactsFetching;
      },
    },
  },
  data(): IData {
    return {
      modalName: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_REQUEST_TESTIMONIAL_MODAL,
      form: {
        emails: [],
        note: DEFAULT_NOTE,
      },
      hasCopiedLink: false,
      loadingState: loadingState.IDLE,
      contacts: [],
      searchQuery: '',
    };
  },
  validations: {
    form: {
      emails: {
        $each: { email: { email } },
      },
    },
  },
  computed: {
    ...mapState('portfolio', ['user']),
    autoCompleteItems(): Array<{ id: string; name: string; email: string; selectDropdownHelperText: string }> {
      return this.contacts.reduce((acc, contact) => {
        if (contact.emails) {
          contact.emails.forEach(email => {
            acc.push({
              id: uid(),
              name: contact.name,
              email,
              selectDropdownHelperText: email,
            });
          });
        }

        return acc;
      }, [] as Array<any>);
    },
    filteredList(): Array<{ id: string; name: string; email: string; selectDropdownHelperText: string }> {
      if (!this.searchQuery) {
        return this.autoCompleteItems;
      }
      return this.autoCompleteItems.filter(({ name, email }) => {
        const searchQuery = this.searchQuery.toLowerCase();
        return name.toLowerCase().includes(searchQuery) || email.toLowerCase().includes(searchQuery);
      });
    },
    googleRedirectURL(): string {
      const query = buildQuery({
        fetch: 'true',
        dynamic_modal: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_REQUEST_TESTIMONIAL_MODAL,
      });

      return `/users/fetch_contacts/google_oauth2?refferer=${process.env.internalBaseUrl}/portfolio/${
        this.$auth.user!.slug
      }${encodeURIComponent(query)}`;
    },
    isButtonDisabled(): boolean {
      return this.loadingState !== loadingState.IDLE;
    },
    areEmailsValid(): boolean {
      return !(this.$v.form.$dirty && this.$v.form.$invalid);
    },
    emailErrorMassage(): string {
      return !this.areEmailsValid ? 'Emails are not valid' : '';
    },
    requestLink(): string {
      return makeUrl(`/p/testify?sign=${this.testifySign}`);
    },
    hasEmail(): boolean {
      return this.form.emails.length > 0;
    },
  },
  methods: {
    onEmailRemove(index: number): void {
      this.form.emails.splice(index, 1);
    },
    async copyRequestLink(): Promise<void> {
      await navigator.clipboard.writeText(this.requestLink as string);
      this.$notice.success('Request link copied');
      this.$analytics.logEvent('public-portfolio__request-testimonial', {
        'copy-link': true,
      });
    },
    onCancelClick(): void {
      this.$modal.hide(this.modalName);
    },
    async handleSave(): Promise<void> {
      if (this.form.emails.length === 0) {
        await this.$modal.hide(this.modalName);
        return;
      }

      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        this.loadingState = loadingState.SUBMITTING;

        this.$analytics.logEvent('public-portfolio__request-testimonial', {
          emails: this.form.emails.length,
          // @ts-ignore
          'import-from-gmail': this.contactsSynced,
          'import-from-outlook': false,
          'customized-note': !!this.form.note && this.form.note !== DEFAULT_NOTE,
        });

        try {
          await this.$apollo.mutate({
            mutation: RequestTestimonialForPublicPortfolioCurrentUser,
            variables: { input: { emails: this.form.emails.map(e => e.email), note: this.form.note } },
            update: (store, { data: { requestTestimonialForPublicPortfolioCurrentUser } }) => {
              const query = {
                query: PublicPortfolioCurrentUserQuery,
              };

              const data = store.readQuery<{ publicPortfolioCurrentUser: PublicPortfolioCurrentUser }>(query);
              if (data?.publicPortfolioCurrentUser) {
                const testimonials = data.publicPortfolioCurrentUser?.testimonials || [];
                testimonials.push(requestTestimonialForPublicPortfolioCurrentUser);
                data.publicPortfolioCurrentUser.testimonials = testimonials;

                store.writeQuery({ ...query, data });
              }
            },
          });

          this.form.emails = [];

          await this.$router.push(
            `?dynamic_modal=${this.$constants.dynamicModals.PUBLIC_PORTFOLIO_REQUEST_TESTIMONIAL_SUCCESS_MODAL}`,
          );
        } catch (e) {
          this.$notice.error(e);
          this.$sentryUtils.error(e);
        } finally {
          this.loadingState = loadingState.IDLE;
        }
      }
    },
    handleChange(items: Array<IItem | { isNew: boolean; name: string }>): void {
      this.form.emails = items.map(i => {
        if ('isNew' in i) {
          return {
            id: uid(),
            name: i.name,
            email: i.name,
          };
        } else {
          return i;
        }
      });
    },
    handleQuery($event: string): void {
      this.searchQuery = $event;
    },
    handleCreate(value: string) {
      return {
        id: value,
        name: value,
      };
    },
  },
});
